<script setup lang="ts">
/**
 * Aura Incorporation — Notice for Controllers (Companies Act s.386AG(2)(a)).
 *
 * Aura 템플릿은 개인·법인 컨트롤러를 **한 문서**에서 다루며 SG 복사본보다 항목이 훨씬 많다.
 * (SG 컴포넌트를 쓰면 40개 변수 중 절반 이상이 입력란조차 없어 빈칸으로 생성된다)
 *
 * ⚠ 조건/반복부는 반드시 fill 에 기록한다 — 화면 로컬 상태로만 두면
 *   [[if Is_Controller]] 등이 거짓으로 평가돼 해당 블록이 통째로 삭제된다(DDF 사례).
 *     · is_controller            : [[if Is_Controller]]
 *     · knows_other_controller   : [[if Knows_Other_Controller]]
 *     · oc{N}_other_controller_* : [[if other_controllers]] 반복 행 → normalize 가 배열로 변환
 */
import { inject, onMounted, ref, watch } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { useCddFill, DOC_SCOPE } from './cdd/useCddFill';
import './allotDoc.css';

const fill = useCddFill();
const scope = inject(DOC_SCOPE, '');
const K = (k: string) => scope + k;

const FLAGS = ['is_controller', 'knows_other_controller'] as const;
const DEFAULTS: Record<string, boolean> = {
  is_controller: true,
  knows_other_controller: false,
};

const isController = ref(DEFAULTS.is_controller);
const knowsOther = ref(DEFAULTS.knows_other_controller);

function readFlag(k: string, fallback: boolean): boolean {
  const v = String(fill.values[K(k)] ?? '').trim().toLowerCase();
  if (!v) return fallback;
  return v === 'yes' || v === 'true' || v === '1';
}
function syncFromFill() {
  isController.value = readFlag('is_controller', DEFAULTS.is_controller);
  knowsOther.value = readFlag('knows_other_controller', DEFAULTS.knows_other_controller);
}
function toggleController() {
  isController.value = !isController.value;
  fill.setValue(K('is_controller'), isController.value ? 'yes' : 'no');
}
function toggleKnowsOther() {
  knowsOther.value = !knowsOther.value;
  fill.setValue(K('knows_other_controller'), knowsOther.value ? 'yes' : 'no');
}

/** 다른 등록가능 컨트롤러 — [[if other_controllers]] 반복 행 */
const otherRows = ref<number[]>([1]);
function addOther() {
  otherRows.value.push((otherRows.value[otherRows.value.length - 1] || 0) + 1);
}
function removeOther(id: number) {
  if (otherRows.value.length > 1) otherRows.value = otherRows.value.filter((r) => r !== id);
}

onMounted(() => {
  for (const k of FLAGS) {
    if (!String(fill.values[K(k)] ?? '').trim()) {
      fill.setValue(K(k), DEFAULTS[k] ? 'yes' : 'no');
    }
  }
  syncFromFill();
  // 이미 입력된 oc 행이 있으면 그 수만큼 표시
  let max = 0;
  for (const key of Object.keys(fill.values)) {
    const m = /^oc(\d+)_other_controller_full_name$/.exec(key.replace(scope, ''));
    if (m && String(fill.values[key] ?? '').trim()) max = Math.max(max, Number(m[1]));
  }
  while (otherRows.value.length < max) otherRows.value.push(otherRows.value.length + 1);
});

watch(
  () => FLAGS.map((k) => fill.values[K(k)]).join('|'),
  () => syncFromFill(),
);
</script>

<template>
  <div class="allot-doc">
    <div class="doc-lbl-title" style="text-align:center">NOTICE FOR CONTROLLERS</div>
    <p class="doc-p">NAME OF COMPANY: <CddVarField field-key="company_name" label="Company Name" /></p>
    <p class="doc-p">UEN NO.: <CddVarField field-key="company_registration_no" label="Company Registration No." /> (the "Company")</p>
    <p class="doc-p">Date of Notice: <CddVarField field-key="notice_date" label="Notice Date" /></p>
    <p class="doc-p">Dear <CddVarField field-key="controller_name" label="Controller Name" /></p>
    <p class="doc-p">
      We know or have reasonable grounds to believe that you are a registrable controller of
      <CddVarField field-key="company_name" label="Company Name" />. This notice is sent under section 386AG(2)(a) of the
      Companies Act (Cap. 50) and requires you to provide the following information within 30 days after the date of this notice.
    </p>

    <p class="doc-p"><b>1. Are you a registrable controller of the Company?</b>
      <span class="doc-toggle" @click="toggleController">{{ isController ? '☒ Yes / ☐ No' : '☐ Yes / ☒ No' }}</span>
    </p>
    <template v-if="isController">
      <p class="doc-sec-title" style="text-align:left">(a) If you are an individual</p>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">(i) your full name</td><td><CddVarField field-key="controller_full_name" label="Controller Full Name" /></td></tr>
          <tr><td class="cdd-lcell">(ii) your aliases, if any</td><td><CddVarField field-key="controller_alias" label="Controller Alias" /></td></tr>
          <tr><td class="cdd-lcell">(iii) your residential address</td><td><CddVarField field-key="controller_residential_address" label="Controller Residential Address" /></td></tr>
          <tr><td class="cdd-lcell">(iv) your nationality</td><td><CddVarField field-key="controller_nationality" label="Controller Nationality" /></td></tr>
          <tr><td class="cdd-lcell">(v) your identity card / passport number</td><td><CddVarField field-key="controller_identification_no" label="Controller Identification No." /></td></tr>
          <tr><td class="cdd-lcell">(vi) your date of birth</td><td><CddVarField field-key="controller_date_of_birth" label="Controller Date of Birth" /></td></tr>
          <tr><td class="cdd-lcell">(vii) date you became an individual controller</td><td><CddVarField field-key="controller_date_became_individual_controller" label="Date Became Individual Controller" /></td></tr>
        </tbody>
      </table>
      <p class="doc-sec-title" style="text-align:left">(b) If you are a legal entity</p>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">(i) your name</td><td><CddVarField field-key="controller_entity_name" label="Controller Entity Name" /></td></tr>
          <tr><td class="cdd-lcell">(ii) your unique entity number, if any</td><td><CddVarField field-key="controller_entity_uen" label="Controller Entity UEN" /></td></tr>
          <tr><td class="cdd-lcell">(iii) address of your registered office</td><td><CddVarField field-key="controller_registered_office_address" label="Controller Registered Office Address" /></td></tr>
          <tr><td class="cdd-lcell">(iv) your legal form</td><td><CddVarField field-key="controller_legal_form" label="Controller Legal Form" /></td></tr>
          <tr><td class="cdd-lcell">(v) jurisdiction and statute of formation</td><td><CddVarField field-key="controller_jurisdiction" label="Controller Jurisdiction" /></td></tr>
          <tr><td class="cdd-lcell">(vi) name of corporate entity register, if applicable</td><td><CddVarField field-key="controller_corporate_register_name" label="Corporate Register Name" /></td></tr>
          <tr><td class="cdd-lcell">(vii) identification / registration number, if applicable</td><td><CddVarField field-key="controller_corporate_register_no" label="Corporate Register No." /></td></tr>
          <tr><td class="cdd-lcell">(viii) date you became a corporate controller</td><td><CddVarField field-key="controller_date_became_corporate_controller" label="Date Became Corporate Controller" /></td></tr>
        </tbody>
      </table>
    </template>

    <p class="doc-p"><b>2. Do you know or have reasonable grounds to believe that any other person is a registrable controller of the Company?</b>
      <span class="doc-toggle" @click="toggleKnowsOther">{{ knowsOther ? '☒ Yes / ☐ No' : '☐ Yes / ☒ No' }}</span>
    </p>
    <template v-if="knowsOther">
      <p class="doc-sec-title" style="text-align:left">(a) If the person is an individual</p>
      <div v-for="id in otherRows" :key="id" class="rep-block">
        <table class="cdd-tbl">
          <tbody>
            <tr><td class="cdd-lcell">(i) full name</td><td><CddVarField :field-key="`oc${id}_other_controller_full_name`" label="Other Controller Full Name" /></td></tr>
            <tr><td class="cdd-lcell">(ii) aliases, if any</td><td><CddVarField :field-key="`oc${id}_other_controller_alias`" label="Other Controller Alias" /></td></tr>
            <tr><td class="cdd-lcell">(iii) residential address</td><td><CddVarField :field-key="`oc${id}_other_controller_residential_address`" label="Other Controller Residential Address" /></td></tr>
            <tr><td class="cdd-lcell">(iv) nationality</td><td><CddVarField :field-key="`oc${id}_other_controller_nationality`" label="Other Controller Nationality" /></td></tr>
            <tr><td class="cdd-lcell">(v) identity card / passport number</td><td><CddVarField :field-key="`oc${id}_other_controller_identification_no`" label="Other Controller Identification No." /></td></tr>
            <tr><td class="cdd-lcell">(vi) date of birth</td><td><CddVarField :field-key="`oc${id}_other_controller_date_of_birth`" label="Other Controller Date of Birth" /></td></tr>
            <tr>
              <td class="cdd-lcell">(vii) date became an individual controller</td>
              <td>
                <CddVarField :field-key="`oc${id}_other_controller_date_became_individual_controller`" label="Date Became Individual Controller" />
                <button v-if="otherRows.length > 1" class="cdd-rowbtn" title="Remove" @click="removeOther(id)">✕</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <button class="cdd-addrow" @click="addOther">＋ Add other controller</button>

      <p class="doc-sec-title" style="text-align:left">(b) If the person is a legal entity</p>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">(i) name</td><td><CddVarField field-key="other_controller_entity_name" label="Other Controller Entity Name" /></td></tr>
          <tr><td class="cdd-lcell">(ii) unique entity number, if any</td><td><CddVarField field-key="other_controller_entity_uen" label="Other Controller Entity UEN" /></td></tr>
          <tr><td class="cdd-lcell">(iii) registered office address</td><td><CddVarField field-key="other_controller_registered_office_address" label="Other Controller Registered Office Address" /></td></tr>
          <tr><td class="cdd-lcell">(iv) legal form</td><td><CddVarField field-key="other_controller_legal_form" label="Other Controller Legal Form" /></td></tr>
          <tr><td class="cdd-lcell">(v) jurisdiction and statute of formation</td><td><CddVarField field-key="other_controller_jurisdiction" label="Other Controller Jurisdiction" /></td></tr>
          <tr><td class="cdd-lcell">(vi) name of corporate entity register, if applicable</td><td><CddVarField field-key="other_controller_corporate_register_name" label="Other Corporate Register Name" /></td></tr>
          <tr><td class="cdd-lcell">(vii) identification / registration number, if applicable</td><td><CddVarField field-key="other_controller_corporate_register_no" label="Other Corporate Register No." /></td></tr>
          <tr><td class="cdd-lcell">(viii) date became a corporate controller</td><td><CddVarField field-key="other_controller_date_became_corporate_controller" label="Date Became Corporate Controller" /></td></tr>
        </tbody>
      </table>
    </template>

    <p class="doc-p" style="margin-top:12px">Yours sincerely,</p>
    <p class="doc-p"><CddVarField field-key="secretary_signature" label="Secretary Signature" /></p>
    <p class="doc-p">Name: <CddVarField field-key="secretary_name" label="Secretary Name" /> — Company Secretary</p>

    <p class="doc-sec-title" style="text-align:left">CONFIRMATION OF PARTICULARS BY REGISTRABLE CONTROLLER</p>
    <p class="doc-p">I/We the undersigned, hereby confirm that the particulars provided by me/us in this notice is (a) true and correct on item (1)*; (b) to my/our best knowledge, true and correct on item (2)*.</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Signature</td><td><CddVarField field-key="controller_signature" label="Controller Signature" /></td></tr>
        <tr><td class="cdd-lcell">Name</td><td><CddVarField field-key="confirming_controller_name" label="Confirming Controller Name" /></td></tr>
        <tr><td class="cdd-lcell">Title</td><td><CddVarField field-key="confirming_controller_title" label="Confirming Controller Title" /></td></tr>
        <tr><td class="cdd-lcell">Date</td><td><CddVarField field-key="dated_on" label="Dated On" /></td></tr>
      </tbody>
    </table>
    <p class="doc-note">* Delete as appropriate</p>
  </div>
</template>

<style scoped>
.doc-note { font-size: 10px; color: #555; font-style: italic; margin-top: 6px; }
.rep-block { margin-bottom: 6px; }
</style>
